import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import type { ChatMessage } from "~/entities/chat";
import { cn } from "~/shared/lib/utils";

import { buildHighlight } from "./highlightMatch";
import { IN_CHAT_SEARCH_MIN_CHARS } from "./useInChatSearch";

function isSameDay(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
    );
}

function formatDateTime(iso: string, locale: string, t: (key: string) => string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    const time = new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
    if (isSameDay(date, today)) {
        return `${t("inChatSearch.today")}, ${time}`;
    }
    if (isSameDay(date, yesterday)) {
        return `${t("inChatSearch.yesterday")}, ${time}`;
    }
    const day = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(date);
    return `${day}, ${time}`;
}

interface InChatSearchResultsProps {
    query: string;
    messages: ChatMessage[];
    isFetching: boolean;
    onSelect: (messageId: string) => void;
}

// Выпадашка под input'ом search-mode в шапке чата. Фиксированная
// высота, внутренний скролл — окно не «прыгает» при разном
// количестве совпадений. Подсветка нужного слова в сниппете
// помогает сразу видеть контекст.
export function InChatSearchResults({ query, messages, isFetching, onSelect }: InChatSearchResultsProps) {
    const { t, i18n } = useTranslation("chat");
    const trimmed = query.trim();

    return (
        <div className="absolute top-full right-0 left-0 z-30 mt-1 overflow-hidden rounded-md border border-zinc-800 bg-zinc-950/95 shadow-lg backdrop-blur-sm">
            {/* Высота 320px на десктопе, на узких экранах режется до
                50vh, чтобы выпадашка не вылезала за viewport. */}
            <div className="h-[min(50vh,320px)] overflow-y-auto">
                {trimmed.length < IN_CHAT_SEARCH_MIN_CHARS ? (
                    <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                        {t("inChatSearch.minChars", { count: IN_CHAT_SEARCH_MIN_CHARS })}
                    </div>
                ) : isFetching && messages.length === 0 ? (
                    <div className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-muted-foreground">
                        <Loader2 className="size-3.5 animate-spin" />
                        {t("inChatSearch.searching")}
                    </div>
                ) : messages.length === 0 ? (
                    <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                        {t("inChatSearch.empty")}
                    </div>
                ) : (
                    <ul className="py-1">
                        {messages.map((message) => (
                            <li key={message.id}>
                                <ResultRow
                                    message={message}
                                    query={trimmed}
                                    locale={i18n.language}
                                    t={t}
                                    onSelect={onSelect}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

interface ResultRowProps {
    message: ChatMessage;
    query: string;
    locale: string;
    t: (key: string) => string;
    onSelect: (messageId: string) => void;
}

function ResultRow({ message, query, locale, t, onSelect }: ResultRowProps) {
    const segments = buildHighlight(message.prompt, query);
    const dateTime = formatDateTime(message.createdAt, locale, t);

    return (
        <button
            type="button"
            onClick={() => onSelect(message.id)}
            className={cn(
                "flex w-full items-start gap-3 px-3 py-2 text-left transition-colors",
                "hover:bg-zinc-900",
            )}
        >
            <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">
                {segments.map((seg, idx) => (
                    <span
                        key={idx}
                        className={seg.match ? "rounded-sm bg-violet-500/30 text-violet-100" : undefined}
                    >
                        {seg.text}
                    </span>
                ))}
            </span>
            <span className="shrink-0 font-mono text-[11px] whitespace-nowrap text-muted-foreground">
                {dateTime}
            </span>
        </button>
    );
}
